import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SCRIPT_LOAD_TIMEOUT_MS = 10000;

interface UseTurnstileResult {
  token: string | null;
  isReady: boolean;
  error: boolean;
  containerRef: React.RefCallback<HTMLElement>;
  resetWidget: () => void;
}

export function useTurnstile(): UseTurnstileResult {
  const siteKey = window.config?.TURNSTILE_SITE_KEY;

  const [token, setToken] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(!siteKey);
  const [error, setError] = useState(false);
  const widgetIdRef = useRef<string | null>(null);
  const containerElRef = useRef<HTMLElement | null>(null);

  const renderWidget = useCallback(() => {
    if (
      !siteKey ||
      !window.turnstile ||
      !containerElRef.current ||
      widgetIdRef.current
    ) {
      return;
    }

    widgetIdRef.current = window.turnstile.render(containerElRef.current, {
      sitekey: siteKey,
      size: "normal",
      callback: (t: string) => {
        setError(false);
        setToken(t);
        setIsReady(true);
      },
      "expired-callback": () => {
        setToken(null);
        setIsReady(false);
        if (widgetIdRef.current) {
          window.turnstile?.reset(widgetIdRef.current);
        }
      },
      "error-callback": () => {
        setError(true);
      },
    });
  }, [siteKey]);

  useEffect(() => {
    if (!siteKey) return;

    // If script is already loaded, render immediately
    if (window.turnstile) {
      renderWidget();
      return;
    }

    // Check if script tag already exists
    const existing = document.querySelector(
      `script[src="${TURNSTILE_SCRIPT_URL}"]`,
    );
    if (existing) {
      // Script tag exists but not loaded yet — wait for it
      const onLoad = () => renderWidget();
      existing.addEventListener("load", onLoad);
      return () => existing.removeEventListener("load", onLoad);
    }

    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;

    const timeout = setTimeout(() => {
      setError(true);
    }, SCRIPT_LOAD_TIMEOUT_MS);

    script.onload = () => {
      clearTimeout(timeout);
      renderWidget();
    };

    script.onerror = () => {
      clearTimeout(timeout);
      setError(true);
    };

    document.head.appendChild(script);

    return () => {
      clearTimeout(timeout);
    };
  }, [siteKey, renderWidget]);

  // Cleanup widget on unmount
  useEffect(() => {
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  const containerRef = useCallback(
    (el: HTMLElement | null) => {
      containerElRef.current = el;
      if (el && siteKey && window.turnstile && !widgetIdRef.current) {
        renderWidget();
      }
    },
    [siteKey, renderWidget],
  );

  const resetWidget = useCallback(() => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      setToken(null);
      setIsReady(false);
      setError(false);
    }
  }, []);

  return { token, isReady, error, containerRef, resetWidget };
}
